import { useRef, useState } from "react";
import { useUnmountLayout } from "@wags/tanstack-start/react";
import type { LocationSelection } from "@/features/viewer-location";

type LocationRequest = () => Promise<LocationSelection | null>;

type RequestState =
    | { status: "idle" }
    | { status: "pending" }
    | { status: "error"; retry: LocationRequest };

export function useLocationRequest({
    onSelected,
    onReadyChange,
}: {
    onSelected: (location: LocationSelection) => void;
    onReadyChange?: (ready: boolean) => void;
}) {
    const [state, setState] = useState<RequestState>({ status: "idle" });
    const requestId = useRef(0);
    useUnmountLayout(() => {
        requestId.current += 1;
    });

    function cancel() {
        requestId.current += 1;
        setState({ status: "idle" });
        onReadyChange?.(true);
    }

    async function select(request: LocationRequest) {
        const id = ++requestId.current;
        setState({ status: "pending" });
        onReadyChange?.(false);
        let location: LocationSelection | null;
        try {
            location = await request();
        } catch {
            location = null;
        }
        if (id !== requestId.current) return;
        if (!location) {
            setState({ status: "error", retry: request });
            return;
        }
        setState({ status: "idle" });
        onSelected(location);
        onReadyChange?.(true);
    }

    return { state, select, cancel };
}
